import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { OrganizationsRepository } from "../repositories/OrganizationsRepository";

/**
 * Whether the signed-in user must give a reason for an audited change to the open organization.
 *
 * True for an Editor who is not the organization's assigned user — temporary access included. The server reads the
 * assignment from the database on every request, so this reads the organization's assigned user too: the sign-in's
 * claim keeps an assignment made or removed since then for as long as the access token lives, and the portal would
 * then skip a reason the server requires. The claim is used only until the organization has loaded. Distinct from
 * `isAssignedUser`, which counts temporary access as assigned for deciding who may act.
 */
export function useRequiresReason(): boolean {
  const { getUserInfo, getUserId, hasManagedAccessToOrganization } = useAuth();
  const { selectedOrganizationId } = useOrganization();
  const organizationsRepository = new OrganizationsRepository();
  const organizationId = selectedOrganizationId ?? "";
  const isEditor = getUserInfo().isEditor();

  // The same key and fetch as the organization pages, so this shares their cached copy rather than adding a request.
  const organizationQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION, organizationId],
    queryFn: async () => await organizationsRepository.getOrganizationWithStatistics(organizationId),
    enabled: isEditor && organizationId.length > 0,
    staleTime: 60 * 1000,
  });

  if (!isEditor) return false;

  const organization = organizationQuery.data;
  if (!organization) return !hasManagedAccessToOrganization(organizationId);

  return organization.assignedUser?.id?.toLowerCase() !== getUserId()?.toLowerCase();
}
